import { Box, Checkbox, CircularProgress, Divider, IconButton, Typography } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SpeakerNotesOffIcon from '@material-ui/icons/SpeakerNotesOff';
import { Dispatch } from '@reduxjs/toolkit';
import CustomPagination from 'components/CustomPagination/CustomPagination';
import SearchInput from 'components/SearchInput/SearchInput';
import { MenuItemsEnum } from 'enums/menuItemsEnum';
import { OrderEnum } from 'enums/orderEnum';
import { StorageKeyEnum } from 'enums/StorageKeyEnum';
import Refresh from 'icons/Refresh';
import React, { ChangeEvent, Component, Fragment } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { IDialogOpenPayload } from 'store/dialog/dialog.interface';
import {
  checkProject,
  getProjectLanguages,
  getProjectWorkflowSteps,
  getXTMCustomersForUser,
  getXTMRequestersForProject,
  setProjectSearch,
  setProjectsPagination,
} from 'store/projects/projects.actions';
import {
  IProjectCheckPayload,
  IProjectState,
  IQueuePagination,
  ProjectCheckArray,
  ProjectErrorArray,
} from 'store/projects/projects.interface';
import {
  getCheckProjectArraySelector,
  getProjectErrorArraySelector,
  getProjectSearchSelector,
  getProjectsSelector,
} from 'store/projects/projects.selectors';
import { getAvailableLanguages } from 'store/settings/settings.actions';
import { getProjectsSpinnerSelector } from 'store/spinner/spinner.selectors';
import { RefreshButton, Spacer, TableToolbarDiv } from 'styles/shared.styled';
import { debounce } from 'utils/debounce';
import { getItem } from 'utils/handlers/storageHandler';
import { isInIFrame } from 'utils/services/kenticoSdk.service';
import BulkActionsButton from './BulkActionsButton/BulkActionsButton';
import ExportActionsButton from './ExportActionsButton/ExportActionsButton';
import FilterMenu from './FilterMenu/FilterMenu';
import ProjectRow from './ProjectRow/ProjectRow';
import QuickActionMenu from './QuickActionMenu/QuickActionMenu';
import {
  DataEmptyDiv,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TranslationProgressDiv,
  TranslationProgressHeaderCheckbox,
  TranslationProgressTableSpinnerDiv,
} from './TranslationProgress.styled';

interface IStateProps {
  projectsState: IProjectState;
  projectsSpinner: boolean;
  checkProjectArray: ProjectCheckArray;
  projectErrorArray: ProjectErrorArray;
  projectSearch: string;
}

interface IDispatchProps {
  setProjectsPagination: (payload: IQueuePagination) => AppDispatch;
  getAvailableLanguages: () => AppDispatch;
  checkProject: (payload: IProjectCheckPayload) => AppDispatch;
  getProjectLanguages: (payload: string) => AppDispatch;
  getProjectWorkflowSteps: (payload: string) => AppDispatch;
  getXTMRequesters: (payload: string) => AppDispatch;
  getXTMCustomers: () => AppDispatch;
  setProjectSearch: (payload: string) => AppDispatch;
}

interface IState {
  searchSpinner: boolean;
  searchValue: string;
  anchorElement: null | HTMLElement;
  currentMenuItem: MenuItemsEnum;
  projectInfo: IDialogOpenPayload['projectInfo'];
}

type PropType = WithTranslation & IDispatchProps & IStateProps;

export class TranslationProgress extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);

    const { projectSearch } = this.props;

    this.state = {
      searchSpinner: false,
      searchValue: projectSearch,
      anchorElement: null,
      currentMenuItem: MenuItemsEnum.PROJECT,
      projectInfo: {},
    };
  }

  componentDidMount(): void {
    const {
      setProjectsPagination,
      getAvailableLanguages,
      getProjectLanguages,
      getProjectWorkflowSteps,
      getXTMCustomers,
      getXTMRequesters,
      projectsState: { pagination },
    } = this.props;
    const projectId = getItem(StorageKeyEnum.projectId);

    setProjectsPagination(pagination);
    getAvailableLanguages();
    getXTMCustomers();
    if (projectId) {
      getProjectLanguages(projectId);
      getProjectWorkflowSteps(projectId);
      getXTMRequesters(projectId);
    }
  }

  componentDidUpdate({ projectSearch: previousSearch }: PropType, { searchValue: previousValue }: IState): void {
    const { searchValue } = this.state;
    const { projectSearch } = this.props;

    if (previousSearch !== projectSearch && (projectSearch !== searchValue || previousValue === searchValue)) {
      this.setState({ searchValue: projectSearch });
    }
  }

  private debounceFn?: Function;

  onSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { setProjectSearch } = this.props;

    this.setState({
      searchSpinner: true,
      searchValue: event.target.value,
    });
    event.persist();

    if (!this.debounceFn) {
      this.debounceFn = debounce(() => {
        this.setState(
          {
            searchSpinner: false,
          },
          () => {
            const { searchValue } = this.state;

            if (!searchValue || searchValue.length >= 3) {
              setProjectSearch(searchValue);
            }
          },
        );
      }, 500);
    }
    this.debounceFn();
  };

  handleRefresh = (): void => {
    const {
      projectsState: { pagination },
      setProjectsPagination,
    } = this.props;

    setProjectsPagination({ ...pagination, forceUpdate: true });
  };

  handleChangeOrder = (header: string, sortable?: boolean): (() => void) => {
    return (): void => {
      if (sortable) {
        const {
          projectsState: { pagination },
          setProjectsPagination,
        } = this.props;
        const { orderBy, order } = pagination;

        const isAsc = orderBy === header && order === OrderEnum.ASC;

        setProjectsPagination({
          ...pagination,
          order: isAsc ? OrderEnum.DESC : OrderEnum.ASC,
          orderBy: header,
        });
      }
    };
  };

  handleChangePage = (page: number): void => {
    const {
      projectsState: { pagination },
      setProjectsPagination,
    } = this.props;

    setProjectsPagination({ ...pagination, page });
  };

  handleChangeRowsPerPage = (size: number): void => {
    const {
      projectsState: { pagination },
      setProjectsPagination,
    } = this.props;

    setProjectsPagination({
      ...pagination,
      size,
      page: 0,
    });
  };

  handleMenuOpen = (menuItem: MenuItemsEnum, projectInfo: IDialogOpenPayload['projectInfo']) => (
    event: React.MouseEvent<HTMLElement>,
  ): void => {
    this.setState({
      anchorElement: event.currentTarget,
      currentMenuItem: menuItem,
      projectInfo,
    });
  };

  handleMenuClose = (): void => {
    this.setState({
      anchorElement: null,
    });
  };

  handleCheckProject = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    const {
      projectsState: { data },
      checkProject,
    } = this.props;
    if (data) {
      const checkProjectPayload = {
        projects: data.map(({ id, targetLanguages, status: projectStatus }) => ({
          id,
          status: projectStatus,
          languages: targetLanguages.map(({ language, files, status: languageStatus }) => ({
            id: language,
            status: languageStatus,
            files: files.map(({ id, status: fileStatus }) => ({
              id,
              checked,
              status: fileStatus,
            })),
          })),
        })),
      };

      checkProject(checkProjectPayload);
    }
  };

  renderHeaders(): JSX.Element {
    const {
      t,
      projectsState: {
        pagination: { orderBy, order },
      },
      checkProjectArray,
    } = this.props;

    const headers = [
      { id: 'project.projectName', sortable: true, width: 16 },
      { id: 'sourceLanguage', sortable: false, width: 10 },
      { id: 'targetLanguages', sortable: false, width: 10 },
      { id: 'customer', sortable: false, width: 10 },
      { id: 'requester', sortable: false, width: 10 },
      { id: 'project.createdAt', sortable: true, width: 8 },
      { id: 'project.dueDate', sortable: true, width: 8 },
      { id: 'workflowStep', sortable: false, width: 10 },
    ];

    if (isInIFrame) {
      headers.push({ id: 'project.projectStatus', sortable: true, width: 18 });
    } else {
      headers.push(
        { id: 'project.progress', sortable: true, width: 10 },
        { id: 'project.projectStatus', sortable: true, width: 8 },
      );
    }

    return (
      <TableHeaderRow>
        <TableCell>
          <Checkbox
            onChange={this.handleCheckProject}
            checked={checkProjectArray.checked}
            indeterminate={checkProjectArray.indeterminate}
          ></Checkbox>
        </TableCell>
        {headers.map((header) => {
          return (
            <TableCell width={header.width} key={header.id}>
              <TableHeader
                sortable={header.sortable}
                sortDirection={order}
                active={header.id == orderBy}
                onClick={this.handleChangeOrder(header.id, header.sortable)}
              >
                <Box fontSize="13px" fontWeight="500" color="#6F8294">
                  {t(`queue.headers.${header.id}`)}
                </Box>
              </TableHeader>
            </TableCell>
          );
        })}
        <TableCell>
          <IconButton size="small" disabled={true} style={{ visibility: 'hidden' }}>
            <ErrorIcon />
          </IconButton>

          <IconButton size="small" disabled={true} style={{ visibility: 'hidden' }}>
            <InfoIcon />
          </IconButton>

          <IconButton size="small" disabled={true} style={{ visibility: 'hidden' }}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableHeaderRow>
    );
  }

  handleFinishedItems = (event: ChangeEvent<HTMLInputElement>, imported: boolean): void => {
    const {
      projectsState: { pagination },
      setProjectsPagination,
    } = this.props;

    setProjectsPagination({
      ...pagination,
      imported: imported ? undefined : false,
    });
  };

  render(): JSX.Element {
    const {
      t,
      projectsSpinner,
      projectsState: { data, pagination, totalPages },
      checkProjectArray,
      projectErrorArray,
    } = this.props;
    const { searchSpinner, searchValue, anchorElement, currentMenuItem, projectInfo } = this.state;

    return (
      <Fragment>
        <QuickActionMenu
          anchorElement={anchorElement}
          handleClose={this.handleMenuClose}
          currentMenuItem={currentMenuItem}
          projectInfo={projectInfo}
        />
        <TranslationProgressDiv>
          <TableToolbarDiv>
            <FilterMenu disabled={searchSpinner || projectsSpinner} />
            <BulkActionsButton disabled={searchSpinner || projectsSpinner} />
            <RefreshButton
              onClick={this.handleRefresh}
              startIcon={<Refresh />}
              disabled={searchSpinner || projectsSpinner}
            >
              <Box fontSize="14px" fontWeight="500">
                {t('queue.refresh')}
              </Box>
            </RefreshButton>
            <Divider variant="middle" orientation="vertical" flexItem />

            <TranslationProgressHeaderCheckbox disabled={projectsSpinner || searchSpinner}>
              <Checkbox
                disabled={searchSpinner || projectsSpinner}
                name="fetchFinishedItems"
                onChange={this.handleFinishedItems}
              />
              <Box fontSize="12px" fontWeight="400" color="#333">
                {t('queue.showFinished')}
              </Box>
            </TranslationProgressHeaderCheckbox>
            <Spacer />
            <ExportActionsButton disabled={searchSpinner || projectsSpinner} />
            <SearchInput
              onChange={this.onSearchChange}
              spinner={searchSpinner || projectsSpinner}
              value={searchValue}
              disabled={projectsSpinner}
            />
          </TableToolbarDiv>

          <TableContainer>
            {this.renderHeaders()}

            {projectsSpinner ? (
              <TranslationProgressTableSpinnerDiv>
                <CircularProgress size={80} color="secondary" />
              </TranslationProgressTableSpinnerDiv>
            ) : data && data?.length ? (
              data.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  handleMenuOpen={this.handleMenuOpen}
                  checkProjectArray={checkProjectArray.projects[project.id]}
                  projectErrorArray={projectErrorArray[project.id]}
                />
              ))
            ) : (
              <DataEmptyDiv>
                <SpeakerNotesOffIcon fontSize="large" />
                <Typography color="textSecondary" variant="caption" component="span">
                  {t('queue.tableEmpty')}
                </Typography>
              </DataEmptyDiv>
            )}
          </TableContainer>

          <CustomPagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            defaultSize={pagination?.size || 10}
            page={pagination?.page || 0}
            totalPages={totalPages || 0}
            onPageChange={this.handleChangePage}
            onRowChange={this.handleChangeRowsPerPage}
          ></CustomPagination>
        </TranslationProgressDiv>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  projectsState: getProjectsSelector(state),
  projectsSpinner: getProjectsSpinnerSelector(state),
  checkProjectArray: getCheckProjectArraySelector(state),
  projectErrorArray: getProjectErrorArraySelector(state),
  projectSearch: getProjectSearchSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  setProjectsPagination: (payload: IQueuePagination): AppDispatch => dispatch(setProjectsPagination(payload)),
  getAvailableLanguages: (): AppDispatch => dispatch(getAvailableLanguages()),
  checkProject: (payload: IProjectCheckPayload): AppDispatch => dispatch(checkProject(payload)),
  getProjectLanguages: (payload: string): AppDispatch => dispatch(getProjectLanguages(payload)),
  getProjectWorkflowSteps: (payload: string): AppDispatch => dispatch(getProjectWorkflowSteps(payload)),
  getXTMCustomers: (): AppDispatch => dispatch(getXTMCustomersForUser()),
  getXTMRequesters: (payload: string): AppDispatch => dispatch(getXTMRequestersForProject(payload)),
  setProjectSearch: (payload: string): AppDispatch => dispatch(setProjectSearch(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(TranslationProgress));
